import { Suspense } from 'react';
import { SCENE_DIMENSION } from '../types';
import { usePartyStore, getEffectiveIntensity } from '../store/partyStore';
import { SCENE_COMPONENTS } from './Scenes';
import { BeatRings, SceneVignette } from './sceneUtils';
import type { SceneProps } from './sceneTypes';

function SceneContent(props: SceneProps & { sceneId: keyof typeof SCENE_COMPONENTS }) {
  const Scene = SCENE_COMPONENTS[props.sceneId];
  return <Scene {...props} />;
}

export default function SceneRenderer() {
  const {
    currentTrack,
    activeEvent,
    beat,
    progressMs,
    displayLyric,
    overrides,
    visualStyle,
  } = usePartyStore();

  if (overrides.blackout) {
    return null;
  }

  const sceneId =
    overrides.sceneLocked && overrides.forcedSceneId
      ? overrides.forcedSceneId
      : activeEvent?.sceneId ?? 'neon-tunnel';
  const sceneOwnsAtmosphere = SCENE_DIMENSION[sceneId] === '3d'
    || sceneId === 'cathedral-of-light'
    || sceneId === 'bird-journey-2d'
    || sceneId === 'doodle-quest-2d'
    || sceneId.endsWith('-visualizer-2d');
  const effectsEnabled = visualStyle !== 'none';

  if (!currentTrack) return null;

  const intensity = getEffectiveIntensity(activeEvent?.intensity ?? 0.6);
  const props: SceneProps = {
    palette: currentTrack.palette,
    intensity,
    beat,
    progressMs,
    lyric: displayLyric,
    albumArtUrl: currentTrack.albumArtUrl,
    visualStyle,
    section: activeEvent?.section ?? 'verse',
    eventStartMs: activeEvent?.startMs ?? 0,
    eventEndMs: activeEvent?.endMs ?? currentTrack.durationMs,
    params: activeEvent?.params ?? {},
  };

  return (
    <Suspense fallback={null}>
      {!sceneOwnsAtmosphere && <SceneVignette {...props} />}
      {effectsEnabled && !sceneOwnsAtmosphere && <BeatRings {...props} />}
      <SceneContent sceneId={sceneId} {...props} />
      {(overrides.forceLyricMode || activeEvent?.params.lyricMode) && displayLyric && sceneId !== 'kinetic-typography' && (
        <SceneContent sceneId="kinetic-typography" {...props} />
      )}
    </Suspense>
  );
}
